import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import classNames from "./Captions.module.css";
import { useDataChannel, useRoomContext } from "@livekit/components-react";
import { Caption, getLanguageName, getTextLanguage } from "../../types";
import useLayout, { useWindowSize } from "../../hooks/layout";
import useCaptions from "../../hooks/captions";
import { IoArrowDown, IoFlagOutline } from "react-icons/io5";
import SimpleBar from "simplebar-react";
import ImproveTranslationDialog from "./dialogs/ImproveTranslationDialog";
import { useMeeting } from "../../hooks/meeting";

const decoder = new TextDecoder();

export function CaptionText(props: { text: string; edited: boolean }) {
  const [layout] = useLayout();

  return (
    <div
      className={classNames.CaptionText}
      style={{
        fontSize: layout.settings.textSize,
        color: layout.settings.textColor,
        backgroundColor: layout.settings.textBackgroundColor,
      }}
    >
      {props.text}
      {props.edited ? (
        <span style={{ color: "#ffab00" }}>{" (edited)"}</span>
      ) : null}
    </div>
  );
}

type CaptionsProps = {
  renderLanguage: string;
  // Set to false to never show speaker name
  showSpeakerName?: boolean;
  maxWidth?: "75%" | "100%";
};

function Captions(props: CaptionsProps) {
  const captionsRef = useRef<HTMLDivElement | null>(null);

  const lastCaptionAdded = useRef(Date.now());

  const [layout] = useLayout();

  const [captions, setCaptions] = useCaptions();

  useDataChannel("caption", (message) => {
    const caption = JSON.parse(decoder.decode(message.payload)) as Caption;
    if (
      Math.abs(Date.now() - lastCaptionAdded.current) <
        layout.settings.textSpeed * 1000 &&
      !caption.complete
    ) {
      return;
    }
    lastCaptionAdded.current = Date.now();
    setCaptions((prevState) => ({
      ...prevState,
      [caption.id]: caption,
    }));
  });

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).addTestCaption = () => {
      const id = Date.now().toString();
      setCaptions((prevState) => ({
        ...prevState,
        [id]: {
          id: id,
          complete: true,
          translations: { ["en"]: "Test test test" },
          name: "Test User",
          identity: crypto.randomUUID(),
          language: "en-US",
        },
      }));
    };
  }, [setCaptions]);

  const lastHeight = useRef(0);
  useEffect(() => {
    if (!captionsRef.current) {
      return;
    }

    const observer = new ResizeObserver(([entry]) => {
      if (entry && entry.contentRect.height !== lastHeight.current) {
        lastHeight.current = entry.contentRect.height;
        // Scroll to bottom
        captionsRef.current?.scrollTo({
          top: captionsRef.current.scrollHeight,
        });
      }
    });

    observer.observe(captionsRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  const [showScrollToBottom, setShowScrollToBottom] = useState(false);

  const checkInAutoScrollThreshold = useCallback(() => {
    if (captionsRef.current) {
      const thresholdInPixels = captionsRef.current.offsetHeight;
      return (
        captionsRef.current.scrollTop + captionsRef.current.offsetHeight >
        captionsRef.current.scrollHeight - thresholdInPixels
      );
    } else {
      return true;
    }
  }, [captionsRef]);

  const selectedLanguage = useMemo(() => {
    return (
      props.renderLanguage && (getTextLanguage(props.renderLanguage) ?? "en")
    );
  }, [props.renderLanguage]);

  const interimCaptionLengths = useMemo(() => {
    return Object.values(captions)
      .filter((c) => !c.complete)
      .reduce((acc: number, curr: Caption) => {
        return acc + (curr.translations[selectedLanguage]?.length ?? 0);
      }, 0);
  }, [captions, selectedLanguage]);

  // Auto scroll captions (when in scroll threshold)
  useEffect(() => {
    if (!showScrollToBottom) {
      // If they are in the threshold of auto scroll, continue to auto scroll on changes
      captionsRef.current?.scrollTo({
        top: captionsRef.current.scrollHeight,
      });
    }

    return () => {};
  }, [captionsRef, showScrollToBottom]);
  const [meeting] = useMeeting();

  const prevScrollTop = useRef<number>(0);
  const prevTranscriptFontSize = useRef<number>(layout.settings.textSize);
  const prevResizeTopHeight = useRef<number>(
    meeting.layout.participantsGridHeightInPercent
  );
  const windowSize = useWindowSize();
  const prevWindowHeight = useRef<number>(windowSize.height);
  const prevInterimCaptionLengths = useRef<number>(interimCaptionLengths);
  const onCaptionsScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      // User scrolled up to stop auto captions if all of the following are true:
      // 1. Transcript font size didn't change
      // 2. Transcript size didn't change
      // 3. Window height didn't change
      // 4. Interim caption lengths didn't change
      const upwardScrollDetected =
        prevScrollTop.current > event.currentTarget.scrollTop &&
        layout.settings.textSize === prevTranscriptFontSize.current &&
        meeting.layout.participantsGridHeightInPercent ===
          prevResizeTopHeight.current &&
        windowSize.height === prevWindowHeight.current;
      interimCaptionLengths === prevInterimCaptionLengths.current;
      prevTranscriptFontSize.current = layout.settings.textSize;
      prevResizeTopHeight.current =
        meeting.layout.participantsGridHeightInPercent;
      prevWindowHeight.current = windowSize.height;
      prevInterimCaptionLengths.current = interimCaptionLengths;
      setShowScrollToBottom(
        upwardScrollDetected || !checkInAutoScrollThreshold()
      );
      prevScrollTop.current = event.currentTarget.scrollTop;
    },
    [
      setShowScrollToBottom,
      layout.settings.textSize,
      meeting.layout.participantsGridHeightInPercent,
      windowSize.height,
      prevScrollTop,
      checkInAutoScrollThreshold,
      interimCaptionLengths,
    ]
  );

  useEffect(() => {
    // Auto-scroll to bottom
    if (!showScrollToBottom) {
      captionsRef.current?.scrollTo({
        top: captionsRef.current.scrollHeight,
      });
    }
  }, [showScrollToBottom, captions]);

  const scrollToBottom = useCallback(() => {
    setShowScrollToBottom(false);
    captionsRef.current?.scrollTo({
      top: captionsRef.current.scrollHeight,
      behavior: "smooth",
    });
  }, [setShowScrollToBottom, captionsRef]);

  const livekitRoom = useRoomContext();

  const [improveCaption, setImproveCaption] = useState<Caption>();
  const [improveTranslationDialogOpen, setImproveTranslationDialogOpen] =
    useState(false);

  // NOTE: In order for auto-scoll to work, parent container needs to be flexbox
  return (
    <>
      <div className={classNames.Captions}>
        {showScrollToBottom && (
          <button
            className={classNames.ScrollToBottom}
            onClick={scrollToBottom}
          >
            Scroll to bottom <IoArrowDown />
          </button>
        )}
        <SimpleBar
          scrollableNodeProps={{ ref: captionsRef }}
          style={{ height: "100%", width: "100%" }}
          onScrollCapture={onCaptionsScroll}
        >
          <div className={classNames.Content}>
            {Object.values(captions)
              .filter((c) => c.translations[selectedLanguage]?.length)
              .reduce<JSX.Element[]>((acc, caption, index, arr) => {
                const lastCaption = index > 0 ? arr[index - 1] : undefined;
                const showName =
                  props.showSpeakerName !== false &&
                  (lastCaption?.name !== caption.name ||
                    lastCaption?.language !== caption.language);

                acc.push(
                  <div
                    key={caption.id}
                    style={{
                      marginTop: index > 0 && showName ? 12 : 0,
                      maxWidth: props.maxWidth || "100%",
                    }}
                  >
                    {showName && (
                      <div className={classNames.CaptionName}>
                        {caption.name}
                        {caption.language
                          ? ` (${
                              getLanguageName(caption.language)?.split(" ")[0]
                            })`
                          : ""}
                      </div>
                    )}
                    <div className={classNames.CaptionAndFlag}>
                      <IoFlagOutline
                        className={classNames.CaptionFlag}
                        onClick={() => {
                          console.log("set caption", caption);
                          setImproveCaption(caption);
                          setImproveTranslationDialogOpen(true);
                        }}
                      />
                      <CaptionText
                        text={caption.translations[selectedLanguage]}
                        edited={!!caption.edited}
                      />
                    </div>
                  </div>
                );

                return acc;
              }, [])}
          </div>
        </SimpleBar>
      </div>
      {improveCaption && (
        <ImproveTranslationDialog
          language={props.renderLanguage}
          livekitRoom={livekitRoom}
          caption={improveCaption}
          open={improveTranslationDialogOpen}
          onOpenChange={setImproveTranslationDialogOpen}
        />
      )}
    </>
  );
}

export default Captions;
